/**
 * @desc Risk & Compliance event cache API slice.
 *
 * Holds fetched ledger events for the Risk & Compliance module. UI state stays in the route or local
 * component state; projections stay pure selectors/helpers.
 *
 * Listens to `farmEvents`' create, update and delete, whoever dispatched them, so a bucket never shows
 * the ledger from before a write:
 *   create  the event joins every loaded bucket whose tenant, farm and types it falls in - no re-read.
 *           A response with no id cannot be placed, so those buckets are marked stale instead.
 *   update  buckets whose types include the event's are marked stale.
 *   delete  the event leaves every bucket by id (the delete does not say what type it was).
 * `useRiskComplianceEvents` re-reads a stale bucket while a tab has it open.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { TIME_LIMIT_FOR_FETCH } from 'app-config/index.js'
import { createEvent, deleteEvent, updateEvent } from 'api/farmEvents'
import { fetchRiskComplianceEvents, READ_MAX_RESULTS } from 'helper-functions/risk-compliance/events/read-events'
import { RISK_COMPLIANCE_EVENT_TYPES } from 'helper-functions/risk-compliance/events/types'

export const RISK_COMPLIANCE_EVENT_VIEWS = {
    register: ['riskRecord', 'riskControl'],
    assessments: ['assessmentRun', 'riskRecord', 'riskControl', 'obligationRecord', 'obligationMet'],
    compliance: ['obligationRecord', 'obligationMet', 'incidentRecord', 'auditRecord', 'visitorRecord', 'biosecurityCleaningRecord', 'trainingRecord', 'farmDocument', 'farmDocumentVersion', 'retentionSetting'],
    documents: ['riskRecord', 'riskControl', 'obligationRecord', 'obligationMet', 'incidentRecord', 'visitorRecord', 'biosecurityCleaningRecord', 'trainingRecord', 'farmDocumentProfile', 'farmDocument', 'farmDocumentVersion'],
}

export const RISK_COMPLIANCE_ALL_TYPES = Object.keys(RISK_COMPLIANCE_EVENT_TYPES)
export const RISK_COMPLIANCE_DEFAULT_VIEW = 'register'

const emptyBucket = () => ({
    items: [],
    status: 'idle',
    error: null,
    truncated: false,
    loadedAt: 0,
    tenant: null,
    farmId: null,
    typesKey: '',
    requestKey: '',
    requestId: null,
    writtenDuringRead: false,
    stale: false,
})

const initialEvents = Object.fromEntries(Object.keys(RISK_COMPLIANCE_EVENT_VIEWS).map(view => [view, emptyBucket()]))

const initialState = {
    events: initialEvents,
    write: {
        status: 'idle',
        error: null,
        written: [],
        lastWriteAt: 0,
    },
}

const typesForView = ({ view = RISK_COMPLIANCE_DEFAULT_VIEW, types } = {}) => (types?.length ? types : RISK_COMPLIANCE_EVENT_VIEWS[view] || RISK_COMPLIANCE_ALL_TYPES)
const typesKey = types => [...types].sort().join(',')
const ttl = () => (typeof TIME_LIMIT_FOR_FETCH === 'number' && TIME_LIMIT_FOR_FETCH > 0 ? TIME_LIMIT_FOR_FETCH : 5 * 60 * 1000)
const sameScope = (bucket, { tenant, farmId, types }) => bucket.tenant === (tenant || null) && `${bucket.farmId ?? ''}` === `${farmId ?? ''}` && bucket.typesKey === typesKey(types)
const cacheFresh = bucket => bucket.loadedAt && !bucket.stale && Date.now() - bucket.loadedAt < ttl()
const requestKeyOf = ({ tenant, farmId, view = RISK_COMPLIANCE_DEFAULT_VIEW, types } = {}) => `${tenant || ''}|${farmId ?? ''}|${typesKey(typesForView({ view, types }))}`

export const fetchRiskComplianceViewEvents = createAsyncThunk(
    'riskCompliance/fetchViewEvents',
    async ({ tenant, farmId, view = RISK_COMPLIANCE_DEFAULT_VIEW, types, force = false, cache = true, maxResults = READ_MAX_RESULTS } = {}, { getState, rejectWithValue }) => {
        const resolvedTypes = typesForView({ view, types })
        const bucket = getState()?.riskCompliance?.events?.[view]

        if (!force && cache && bucket && sameScope(bucket, { tenant, farmId, types: resolvedTypes }) && cacheFresh(bucket)) {
            return { cached: true, view, tenant: tenant || null, farmId: farmId ?? null, types: resolvedTypes, events: bucket.items, truncated: bucket.truncated, loadedAt: bucket.loadedAt }
        }

        try {
            const { events, truncated } = await fetchRiskComplianceEvents({ tenant, farmId, types: resolvedTypes, maxResults })
            return { cached: false, view, tenant: tenant || null, farmId: farmId ?? null, types: resolvedTypes, events, truncated, loadedAt: Date.now() }
        } catch (err) {
            return rejectWithValue(err?.response?.data || err?.message || 'fetchRiskComplianceViewEvents failed')
        }
    },
    {
        // Several components mount on one view at once; an unforced read of a scope already in flight shares that request.
        condition: ({ view = RISK_COMPLIANCE_DEFAULT_VIEW, force = false, ...arg } = {}, { getState }) => {
            const bucket = getState()?.riskCompliance?.events?.[view]
            return force || !(bucket?.status === 'loading' && bucket.requestKey === requestKeyOf({ view, ...arg }))
        },
    },
)

export const writeRiskComplianceEvents = createAsyncThunk('riskCompliance/writeEvents', async ({ events = [], tenant } = {}, { dispatch, rejectWithValue }) => {
    const written = []
    try {
        for (const data of events) {
            // Sequential because event order is part of the ledger contract.
            // eslint-disable-next-line no-await-in-loop
            const result = await dispatch(createEvent({ data, tenant }))
            const fulfilled = typeof result?.type === 'string' && result.type.endsWith('/fulfilled')
            if (!fulfilled) {
                const payload = result?.payload
                return rejectWithValue({ written, error: typeof payload === 'string' ? payload : payload?.error || result?.error?.message || 'Event write failed' })
            }
            written.push({ event: data, id: result.payload?.id ?? null, saved: result.payload })
        }
        return { written, writtenAt: Date.now() }
    } catch (err) {
        return rejectWithValue({ written, error: err?.message || 'writeRiskComplianceEvents failed' })
    }
})

// A bucket holds the event when the read that filled it would have returned it.
const bucketHolds = (bucket, event, tenant) =>
    bucket.loadedAt > 0 && bucket.tenant === (tenant || null) && bucket.typesKey.split(',').includes(event?.type) && (event?.farmIds ?? [event?.farmId]).some(id => id != null && `${id}` === `${bucket.farmId ?? ''}`)

// The created event as a read would return it: the body sent, plus what the API assigned.
const createdEvent = action => {
    const payload = action.payload && typeof action.payload === 'object' ? action.payload : {}
    return { ...action.meta?.arg?.data, ...payload }
}

const riskComplianceSlice = createSlice({
    name: 'riskCompliance',
    initialState,
    reducers: {
        invalidateRiskComplianceEvents(state, action) {
            const views = action.payload?.views || Object.keys(state.events)
            views.forEach(view => {
                if (state.events[view]) state.events[view].stale = true
            })
        },
        clearRiskComplianceEvents(state, action) {
            const views = action.payload?.views || Object.keys(state.events)
            views.forEach(view => {
                if (state.events[view]) state.events[view] = emptyBucket()
            })
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchRiskComplianceViewEvents.pending, (state, action) => {
            const view = action.meta?.arg?.view || RISK_COMPLIANCE_DEFAULT_VIEW
            if (!state.events[view]) state.events[view] = emptyBucket()
            state.events[view].status = 'loading'
            state.events[view].error = null
            state.events[view].requestKey = requestKeyOf(action.meta?.arg)
            state.events[view].requestId = action.meta.requestId
        })
        builder.addCase(fetchRiskComplianceViewEvents.fulfilled, (state, action) => {
            const { view, tenant, farmId, types, events, truncated, loadedAt, cached } = action.payload
            if (!state.events[view]) state.events[view] = emptyBucket()
            // The last read started wins - a read from before a write must not land over the one after it.
            if (state.events[view].requestId && state.events[view].requestId !== action.meta.requestId) return
            state.events[view].status = 'loaded'
            state.events[view].error = null
            if (!cached) {
                state.events[view].items = Array.isArray(events) ? events : []
                state.events[view].truncated = Boolean(truncated)
                state.events[view].loadedAt = loadedAt
                state.events[view].tenant = tenant || null
                state.events[view].farmId = farmId ?? null
                state.events[view].typesKey = typesKey(types)
                state.events[view].stale = state.events[view].writtenDuringRead
            }
            state.events[view].writtenDuringRead = false
        })
        builder.addCase(fetchRiskComplianceViewEvents.rejected, (state, action) => {
            const view = action.meta?.arg?.view || RISK_COMPLIANCE_DEFAULT_VIEW
            if (!state.events[view]) state.events[view] = emptyBucket()
            if (state.events[view].requestId && state.events[view].requestId !== action.meta.requestId) return
            state.events[view].status = 'error'
            state.events[view].error = action.payload || action.error?.message || 'fetchRiskComplianceViewEvents error'
        })

        builder.addCase(writeRiskComplianceEvents.pending, state => {
            state.write.status = 'writing'
            state.write.error = null
        })
        builder.addCase(writeRiskComplianceEvents.fulfilled, (state, action) => {
            state.write.status = 'written'
            state.write.error = null
            state.write.written = action.payload.written
            state.write.lastWriteAt = action.payload.writtenAt
        })
        builder.addCase(writeRiskComplianceEvents.rejected, (state, action) => {
            state.write.status = 'error'
            state.write.error = action.payload?.error || action.payload || action.error?.message || 'writeRiskComplianceEvents error'
            state.write.written = action.payload?.written || []
        })

        builder.addCase(createEvent.fulfilled, (state, action) => {
            const event = createdEvent(action)
            Object.values(state.events).forEach(bucket => {
                // A read in flight may have been answered before this write; re-read once it lands.
                if (bucket.status === 'loading' && bucket.requestKey.split('|')[2]?.split(',').includes(event.type)) bucket.writtenDuringRead = true
                if (!bucketHolds(bucket, event, action.meta?.arg?.tenant)) return
                if (event.id == null) bucket.stale = true
                else if (!bucket.items.some(item => `${item.id}` === `${event.id}`)) bucket.items.push(event)
            })
        })
        builder.addCase(updateEvent.fulfilled, (state, action) => {
            const event = createdEvent(action)
            Object.values(state.events).forEach(bucket => {
                if (bucketHolds(bucket, event, action.meta?.arg?.tenant)) bucket.stale = true
            })
        })
        builder.addCase(deleteEvent.fulfilled, (state, action) => {
            const id = `${action.meta?.arg?.id}`
            Object.values(state.events).forEach(bucket => {
                if (bucket.items.some(item => `${item.id}` === id)) bucket.items = bucket.items.filter(item => `${item.id}` !== id)
            })
        })
    },
})

export const { invalidateRiskComplianceEvents, clearRiskComplianceEvents } = riskComplianceSlice.actions

export const selectRiskComplianceEventBucket = (state, view = RISK_COMPLIANCE_DEFAULT_VIEW) => state?.riskCompliance?.events?.[view] || emptyBucket()
export const selectRiskComplianceEvents = (state, view = RISK_COMPLIANCE_DEFAULT_VIEW) => selectRiskComplianceEventBucket(state, view).items
export const selectRiskComplianceEventStatus = (state, view = RISK_COMPLIANCE_DEFAULT_VIEW) => selectRiskComplianceEventBucket(state, view).status
export const selectRiskComplianceWriteStatus = state => state?.riskCompliance?.write?.status || 'idle'

export default riskComplianceSlice.reducer
